import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { GRACKLE_COLORS, CATEGORICAL, ACTIVITY_TYPE_COLORS } from '@/lib/theme'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateCallLog, usePhoneLookup } from '@/hooks/use-api'
import { PhoneIncoming, Search, User, Building2, Loader2 } from 'lucide-react'
import type { CallDisposition } from '@/types'
import { explainError } from '@/lib/explain-error'

interface LogIncomingCallDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DISPOSITION_OPTIONS: { value: CallDisposition; label: string }[] = [
  { value: 'Connected', label: 'Connected' },
  { value: 'No Answer', label: 'No Answer' },
  { value: 'Left Voicemail', label: 'Left Voicemail' },
  { value: 'Busy', label: 'Busy' },
  { value: 'Wrong Number', label: 'Wrong Number' },
  { value: 'Disconnected', label: 'Disconnected' },
  { value: 'Gatekeeper', label: 'Gatekeeper' },
]

export function LogIncomingCallDialog({ open, onOpenChange }: LogIncomingCallDialogProps) {
  const { toast } = useToast()
  const createCallLog = useCreateCallLog()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [disposition, setDisposition] = useState<CallDisposition>('Connected')
  const [subject, setSubject] = useState('')
  const [notes, setNotes] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [selectedEntity, setSelectedEntity] = useState<{
    id: string
    type: 'Contact' | 'Lead' | 'Account'
    name: string
    accountId?: string
  } | null>(null)

  const { data: lookupResults, isLoading: lookupLoading } = usePhoneLookup(phoneNumber)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPhoneNumber('')
      setDisposition('Connected')
      setSubject('')
      setNotes('')
      setDurationMinutes('')
      setSelectedEntity(null)
    }
  }, [open])

  const handleSelectEntity = (
    id: string,
    type: 'Contact' | 'Lead' | 'Account',
    name: string,
    accountId?: string
  ) => {
    setSelectedEntity({ id, type, name, accountId })
  }

  const handleSubmit = async () => {
    if (!phoneNumber) {
      toast({
        title: 'Error',
        description: 'Phone number is required',
        variant: 'destructive',
      })
      return
    }

    try {
      await createCallLog.mutateAsync({
        direction: 'Inbound',
        phone_number: phoneNumber,
        duration_seconds: durationMinutes ? parseInt(durationMinutes) * 60 : 0,
        started_at: new Date().toISOString(),
        ended_at: new Date().toISOString(),
        disposition,
        subject: subject || `Inbound call - ${selectedEntity?.name || phoneNumber}`,
        notes: notes || undefined,
        who_id: selectedEntity?.type !== 'Account' ? selectedEntity?.id : undefined,
        who_type: selectedEntity?.type !== 'Account' ? selectedEntity?.type : undefined,
        what_id:
          selectedEntity?.type === 'Account' ? selectedEntity?.id : selectedEntity?.accountId,
        what_type:
          selectedEntity?.type === 'Account' || selectedEntity?.accountId ? 'Account' : undefined,
      })

      toast({
        title: 'Call Logged',
        description: 'Incoming call logged successfully.',
      })

      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to log call'),
        variant: 'destructive',
      })
    }
  }

  const totalResults = lookupResults?.total || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        aria-describedby="log-incoming-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneIncoming
              className="h-5 w-5"
              aria-hidden="true"
              style={{ color: ACTIVITY_TYPE_COLORS.Call.solid }}
            />
            Log Incoming Call
          </DialogTitle>
          <DialogDescription id="log-incoming-description">
            Search for a contact, lead, or account by phone number and log the call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Phone Number Search */}
          <div>
            <Label htmlFor="incoming-phone">Phone Number</Label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="incoming-phone"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value)
                  setSelectedEntity(null)
                }}
                placeholder="Enter phone number to search..."
                className="pl-10"
                aria-label="Search by phone number"
              />
            </div>
          </div>

          {/* Search Results */}
          {phoneNumber.length >= 3 && (
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {lookupLoading ? (
                <div className="flex items-center justify-center p-4" role="status">
                  <Loader2
                    className="h-5 w-5 animate-spin text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                </div>
              ) : totalResults === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  No matching records found
                </div>
              ) : (
                <div className="divide-y" role="listbox" aria-label="Phone lookup results">
                  {lookupResults?.contacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() =>
                        handleSelectEntity(
                          contact.id,
                          'Contact',
                          `${contact.first_name} ${contact.last_name}`,
                          contact.account_id || undefined
                        )
                      }
                      className={`w-full text-left p-3 hover:bg-muted transition-colors flex items-center gap-3 ${selectedEntity?.id === contact.id ? 'bg-muted' : ''}`}
                      role="option"
                      aria-selected={selectedEntity?.id === contact.id}
                    >
                      <User
                        className="h-4 w-4 flex-shrink-0"
                        aria-hidden="true"
                        style={{ color: CATEGORICAL.navy.solid }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {contact.first_name} {contact.last_name}
                          {contact.do_not_call && (
                            <span className="ml-1" style={{ color: GRACKLE_COLORS.darkRaspberry }}>
                              (DNC)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          Contact {contact.account_name ? `at ${contact.account_name}` : ''}{' '}
                          &middot; {contact.phone || contact.mobile_phone}
                        </p>
                      </div>
                    </button>
                  ))}
                  {lookupResults?.leads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() =>
                        handleSelectEntity(lead.id, 'Lead', `${lead.first_name} ${lead.last_name}`)
                      }
                      className={`w-full text-left p-3 hover:bg-muted transition-colors flex items-center gap-3 ${selectedEntity?.id === lead.id ? 'bg-muted' : ''}`}
                      role="option"
                      aria-selected={selectedEntity?.id === lead.id}
                    >
                      <User
                        className="h-4 w-4 flex-shrink-0"
                        aria-hidden="true"
                        style={{ color: CATEGORICAL.evergreen.solid }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          Lead {lead.company ? `at ${lead.company}` : ''} &middot;{' '}
                          {lead.phone || lead.mobile_phone}
                        </p>
                      </div>
                    </button>
                  ))}
                  {lookupResults?.accounts.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => handleSelectEntity(account.id, 'Account', account.name)}
                      className={`w-full text-left p-3 hover:bg-muted transition-colors flex items-center gap-3 ${selectedEntity?.id === account.id ? 'bg-muted' : ''}`}
                      role="option"
                      aria-selected={selectedEntity?.id === account.id}
                    >
                      <Building2
                        className="h-4 w-4 flex-shrink-0"
                        aria-hidden="true"
                        style={{ color: CATEGORICAL.plum.solid }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{account.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          Account &middot; {account.phone}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Entity Display */}
          {selectedEntity && (
            <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
              <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">{selectedEntity.name}</span>
              <span className="text-xs text-muted-foreground">({selectedEntity.type})</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto h-6 px-2"
                onClick={() => setSelectedEntity(null)}
                aria-label="Clear selected entity"
              >
                Clear
              </Button>
            </div>
          )}

          {/* Disposition */}
          <div>
            <Label htmlFor="incoming-disposition">Disposition</Label>
            <Select value={disposition} onValueChange={(v) => setDisposition(v as CallDisposition)}>
              <SelectTrigger id="incoming-disposition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISPOSITION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Duration */}
          <div>
            <Label htmlFor="incoming-duration">Duration (minutes)</Label>
            <Input
              id="incoming-duration"
              type="number"
              min="0"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              placeholder="Enter call duration"
            />
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="incoming-subject">Subject</Label>
            <Input
              id="incoming-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Inbound call - ${selectedEntity?.name || phoneNumber || '...'}`}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="incoming-notes">Notes</Label>
            <Textarea
              id="incoming-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Call notes..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!phoneNumber || createCallLog.isPending}
              style={{ backgroundColor: GRACKLE_COLORS.navy, color: '#ffffff' }}
              className="hover:opacity-90"
            >
              {createCallLog.isPending ? 'Saving...' : 'Log Call'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
